import React from "react";
import { AddActionButton } from "../AddActionButton";
import { ReportFiltersEditor } from "./ReportFiltersEditor";
import { ReportDatePicker } from "../ReportDatePicker";
import { SelectPicker } from "../SelectPicker";
import { addChipValue, removeChipValue } from "../ValueMultiSelect";
import {
    PERIOD_END_OPERATOR_OPTIONS,
    PERIOD_IF_BLANK_OPTIONS,
    PERIOD_START_OPERATOR_OPTIONS,
    attachModel,
    clone,
    dataColumnSelectionKey,
    defaultEndOperator,
    isCustomReportingPeriod,
    isLastNReportingPeriod,
    lastNDefault,
    lastNLimit,
    makePeriodBounds,
    normalizeReportingPeriodType,
    periodBoundsIssues,
    periodBoundsPhrase,
} from "../../reportConfigurationLogic";
import type { DataColumn, DataModel, ModelKey, PeriodBoundSide, PeriodBounds, PeriodEndOperator, PeriodIfBlank, PeriodIfBlankKind, PeriodStartOperator, ReportConfig, ReportConfigurationLookupOption } from "../../types";
import { IconAction } from "../shared/EditorControls";

type ReportSetupEditorProps = {
    report: ReportConfig;
    catalog: {
        modelOptions: { key: string; dataModelId: number | undefined; label: string; sortOrder: number; table: string; }[];
        reportingPeriodOptions: ReportConfigurationLookupOption[] | { id: string; value: string; name: string; }[];
        models: Record<string, DataModel>;
        dataModelIdForKey: (modelKey: string) => number | undefined;
        dataModelsLoading: boolean;
        dataModelsError: string;
    };
    session: {
        modelToAdd: string;
        setModelToAdd: React.Dispatch<React.SetStateAction<string>>;
        reportInvoiceNumber: string;
        setReportInvoiceNumber: React.Dispatch<React.SetStateAction<string>>;
    };
    editing: {
        replaceSelectedReport: (updater: (report: ReportConfig) => ReportConfig) => void;
    };
};

export function ReportSetupEditor({ report, catalog, session, editing }: ReportSetupEditorProps) {
    const { modelOptions, reportingPeriodOptions, models, dataModelIdForKey, dataModelsLoading, dataModelsError } = catalog;
    const { modelToAdd, setModelToAdd, reportInvoiceNumber, setReportInvoiceNumber } = session;
    const { replaceSelectedReport } = editing;

    const asOptions = <T extends string>(options: { value: T; name: string }[]) => options.map((option) => ({ id: option.value, value: option.value, name: option.name }));

    function updateBounds(modelKey: ModelKey, updater: (bounds: PeriodBounds) => PeriodBounds | null) {
        replaceSelectedReport((current) => {
            const all = { ...(current.modelPeriodBounds || {}) };
            const next = updater(all[modelKey] || makePeriodBounds(current));
            if (next) all[modelKey] = next;
            else delete all[modelKey];
            return { ...current, modelPeriodBounds: all };
        });
    }

    // Start / end: each bound has its own operator, date column (default: the Based On column)
    // and "if blank" fallback. Unticked = the legacy period clause (every report saved before this existed).
    const sideIfBlank = (bounds: PeriodBounds, side: PeriodBoundSide) => (side === "start" ? bounds.startIfBlank : bounds.endIfBlank) || bounds.ifBlank;

    function updateSide(modelKey: ModelKey, side: PeriodBoundSide, patch: { column?: string; ifBlank?: PeriodIfBlank }) {
        updateBounds(modelKey, (current) => {
            // The first per-side edit splits a legacy shared fallback into explicit start / end ones.
            const { ifBlank: legacy, ...rest } = current;
            const next: PeriodBounds = { ...rest, startIfBlank: current.startIfBlank || legacy, endIfBlank: current.endIfBlank || legacy };
            if ("column" in patch) {
                if (side === "start") next.startColumn = patch.column;
                else next.endColumn = patch.column;
            }
            if (patch.ifBlank) {
                if (side === "start") next.startIfBlank = patch.ifBlank;
                else next.endIfBlank = patch.ifBlank;
            }
            return next;
        });
    }

    function renderBoundRow(report: ReportConfig, modelKey: ModelKey, bounds: PeriodBounds, side: PeriodBoundSide, timestampColumns: DataColumn[], basedOnColumn?: DataColumn) {
        const label = side === "start" ? "Start" : "End";
        const endOperator = bounds.endOperator || defaultEndOperator(report.reportingPeriod.type);
        const basedOnKey = dataColumnSelectionKey(basedOnColumn);
        const columnKey = (side === "start" ? bounds.startColumn : bounds.endColumn) || basedOnKey;
        const column = timestampColumns.find((item) => dataColumnSelectionKey(item) === columnKey || item.key === columnKey);
        const ifBlank = sideIfBlank(bounds, side);
        const ifBlankKind: PeriodIfBlankKind = ifBlank?.kind || "none";
        const otherColumns = timestampColumns.filter((item) => dataColumnSelectionKey(item) !== dataColumnSelectionKey(column));
        const columnOptions = (items: DataColumn[]) => items.map((item) => ({ id: dataColumnSelectionKey(item), value: dataColumnSelectionKey(item), name: item.label, sortOrder: item.sortOrder }));
        const operatorPicker = side === "start"
            ? <SelectPicker options={asOptions(PERIOD_START_OPERATOR_OPTIONS)} value={bounds.startOperator} onChange={(value) => updateBounds(modelKey, (current) => ({ ...current, startOperator: value as PeriodStartOperator }))} searchable={false} />
            : <SelectPicker options={asOptions(PERIOD_END_OPERATOR_OPTIONS)} value={endOperator} onChange={(value) => updateBounds(modelKey, (current) => ({ ...current, endOperator: value as PeriodEndOperator }))} searchable={false} />;
        const showColumn = side === "start" || endOperator !== "none";
        // Reads left to right as a sentence: "<Start Date Column> is <on or after> the period start".
        return (
            <div className="rc-inline-fields compact rc-period-bound-row" key={side}>
                {showColumn && <label><span>{label} Date Column</span><SelectPicker options={columnOptions(timestampColumns)} value={dataColumnSelectionKey(column)} placeholder="Select A Date Column..." onChange={(value) => updateSide(modelKey, side, { column: value === basedOnKey ? undefined : value })} searchPlaceholder="Search Date Columns..." /></label>}
                <label><span>{showColumn ? "Is" : `${label} Date`}</span>{operatorPicker}</label>
                {showColumn && <span className="rc-period-bound-target">the period {side === "start" ? "start" : "end"}</span>}
                {showColumn && (
                    <>
                        <label><span>If {column?.label || "Date"} Is Blank</span><SelectPicker options={asOptions(PERIOD_IF_BLANK_OPTIONS.filter((option) => option.value !== "column" || otherColumns.length))} value={ifBlankKind} onChange={(value) => updateSide(modelKey, side, { ifBlank: { kind: value as PeriodIfBlankKind, column: value === "column" ? dataColumnSelectionKey(otherColumns[0]) : undefined, date: value === "date" ? "2199-12-31" : undefined } })} searchable={false} /></label>
                        {ifBlankKind === "column" && <label><span>Use Column</span><SelectPicker options={columnOptions(otherColumns)} value={ifBlank?.column || ""} placeholder="Select A Date Column..." onChange={(value) => updateSide(modelKey, side, { ifBlank: { kind: "column", column: value } })} searchPlaceholder="Search Date Columns..." /></label>}
                        {ifBlankKind === "date" && <label><span>Use Date</span><ReportDatePicker value={ifBlank?.date || ""} onChange={(value) => updateSide(modelKey, side, { ifBlank: { kind: "date", date: value } })} /></label>}
                    </>
                )}
            </div>
        );
    }

    function renderPeriodBounds(report: ReportConfig, modelKey: ModelKey, timestampColumns: DataColumn[], basedOnColumn?: DataColumn) {
        const bounds = report.modelPeriodBounds?.[modelKey];
        const issues = bounds ? periodBoundsIssues(report, modelKey) : [];
        const phrase = bounds ? periodBoundsPhrase(report, modelKey) : "";
        return (
            <div className="rc-model-period-bounds" data-rc-validation={`model-period-bounds-${modelKey}`}>
                <label className="rc-cb">
                    <input type="checkbox" checked={!!bounds} onChange={(event) => updateBounds(modelKey, () => event.target.checked ? makePeriodBounds(report) : null)} /> Define start / end dates, their columns and what to do when a date is blank
                </label>
                {bounds && (
                    <>
                        {renderBoundRow(report, modelKey, bounds, "start", timestampColumns, basedOnColumn)}
                        {renderBoundRow(report, modelKey, bounds, "end", timestampColumns, basedOnColumn)}
                        <p className="rc-hint">Rows count when {phrase}. Period dates are calculated from the server date (GETDATE()).</p>
                        {issues.length > 0 && <ul className="rc-rule-issues">{issues.map((message) => <li key={message}>{message}</li>)}</ul>}
                    </>
                )}
            </div>
        );
    }

    function renderSetupTab(report: ReportConfig) {
        const availableModels = modelOptions.filter((option) => !report.models.includes(option.key));
        const selectedModelToAdd = availableModels.find((option) => String(option.dataModelId) === modelToAdd);
        return (
            <>
                <section className="rc-block">
                    <h3>Reporting Period</h3>
                    <p className="rc-block-hint">Which window of data this report covers each time it runs. Turn it off for a whole data model below, or for individual metrics on the Metrics tab.</p>
                    <div data-rc-validation="reporting-period">
                        <SelectPicker options={reportingPeriodOptions} value={normalizeReportingPeriodType(report.reportingPeriod.type)} onChange={(value) => replaceSelectedReport((current) => ({
                            ...current,
                            // Switching to Last N Days / Months seeds a sensible N (30 days / 3 months) when none is set.
                            reportingPeriod: { ...current.reportingPeriod, type: value, lastN: isLastNReportingPeriod(value) && current.reportingPeriod.lastN == null ? lastNDefault(value) : current.reportingPeriod.lastN },
                        }))} searchPlaceholder="Search Reporting Periods..." />
                    </div>
                    {isLastNReportingPeriod(report.reportingPeriod.type) && (
                        <div className="rc-inline-fields compact">
                            <label data-rc-validation="reporting-period-last-n">
                                <span>{normalizeReportingPeriodType(report.reportingPeriod.type) === "LastNMonths" ? "Number Of Complete Months" : "Number Of Days"}</span>
                                <input
                                    type="number"
                                    min="0"
                                    max={lastNLimit(report.reportingPeriod.type)}
                                    step="1"
                                    value={report.reportingPeriod.lastN ?? ""}
                                    onChange={(event) => {
                                        const raw = event.target.value;
                                        replaceSelectedReport((current) => ({ ...current, reportingPeriod: { ...current.reportingPeriod, lastN: raw === "" ? undefined : Math.trunc(Number(raw)) } }));
                                    }}
                                />
                            </label>
                        </div>
                    )}
                    {isLastNReportingPeriod(report.reportingPeriod.type) && <p className="rc-hint">{normalizeReportingPeriodType(report.reportingPeriod.type) === "LastNMonths"
                        ? "The last N full calendar months before the current month - e.g. N = 3 run in October covers July 1 to September 30. N = 0 covers the current month to date."
                        : "From N days before the run date onwards - e.g. N = 45 covers the last 45 days. N = 0 covers today."}</p>}
                    {isCustomReportingPeriod(report.reportingPeriod.type) && <div className="rc-inline-fields compact"><label data-rc-validation="reporting-period-custom-start"><span>From</span><ReportDatePicker value={report.reportingPeriod.customStart} onChange={(value) => replaceSelectedReport((current) => ({ ...current, reportingPeriod: { ...current.reportingPeriod, customStart: value } }))} /></label><label data-rc-validation="reporting-period-custom-end"><span>To</span><ReportDatePicker value={report.reportingPeriod.customEnd} onChange={(value) => replaceSelectedReport((current) => ({ ...current, reportingPeriod: { ...current.reportingPeriod, customEnd: value } }))} /></label></div>}
                </section>
                <section className="rc-block" data-rc-validation="data-models">
                    <h3>Data Models Used in This Report</h3>
                    <p className="rc-block-hint">Pick which underlying data this report needs.</p>
                    {report.models.length === 0 && <p className="rc-hint">None yet - add one below.</p>}
                    <div className="rc-model-list">
                        {report.models.map((modelKey) => {
                            const model = models[modelKey];
                            const modelId = dataModelIdForKey(modelKey);
                            if (!model) return null;
                            const timestampColumns = [...model.timestampColumns]
                                .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER) || a.label.localeCompare(b.label));
                            const selectedBasedOn = String(report.modelBasedOn[modelKey] || "");
                            const basedOnColumn = timestampColumns.find((column) => dataColumnSelectionKey(column) === selectedBasedOn || column.key === selectedBasedOn)
                                || timestampColumns[0];
                            const basedOn = dataColumnSelectionKey(basedOnColumn);
                            // Absent = on (every report saved before the switch existed).
                            const appliesPeriod = report.modelApplyReportingPeriod?.[modelKey] !== false;

                            return (
                                <div className="rc-model-row selected" key={modelKey}>
                                    <div className="rc-model-summary">
                                        <span className="rc-modelpill">{model.label}</span>
                                        <label className="rc-cb rc-model-apply-period" title="When off, the Reporting Period date range is left out of the query for every metric on this data model.">
                                            <input
                                                type="checkbox"
                                                checked={appliesPeriod}
                                                onChange={(event) => {
                                                    const checked = event.target.checked;
                                                    replaceSelectedReport((current) => {
                                                        const next = { ...(current.modelApplyReportingPeriod || {}) };
                                                        // Only "off" is stored; on is the default.
                                                        if (checked) delete next[modelKey];
                                                        else next[modelKey] = false;
                                                        return { ...current, modelApplyReportingPeriod: next };
                                                    });
                                                }}
                                            /> Apply Reporting Period
                                        </label>
                                    </div>
                                    <div className={`rc-model-based-on${appliesPeriod ? "" : " is-off"}`} data-rc-validation={`model-based-on-${modelKey}`}>
                                        <span>Reporting Period Based On:</span>
                                        {model.timestampColumns.length ? (
                                            <SelectPicker options={timestampColumns.map((column) => ({ id: dataColumnSelectionKey(column), value: dataColumnSelectionKey(column), name: column.label, sortOrder: column.sortOrder }))} value={basedOn} onChange={(value) => replaceSelectedReport((current) => ({ ...current, modelBasedOn: { ...current.modelBasedOn, [modelKey]: value } }))} searchPlaceholder="Search Date Columns..." disabled={!appliesPeriod} />
                                        ) : (
                                            <span className="rc-hint">No timestamp columns configured</span>
                                        )}
                                    </div>
                                    <IconAction label="Remove Data Model" icon="link_off" onClick={() => replaceSelectedReport((current) => {
                                        const nextModelBasedOn = { ...current.modelBasedOn };
                                        delete nextModelBasedOn[modelKey];
                                        const nextPeriodBounds = { ...(current.modelPeriodBounds || {}) };
                                        delete nextPeriodBounds[modelKey];
                                        const nextApplyPeriod = { ...(current.modelApplyReportingPeriod || {}) };
                                        delete nextApplyPeriod[modelKey];
                                        return {
                                            ...current,
                                            models: current.models.filter((item) => item !== modelKey),
                                            dataModelIds: (current.dataModelIds || []).filter((id) => id !== modelId),
                                            modelBasedOn: nextModelBasedOn,
                                            modelPeriodBounds: nextPeriodBounds,
                                            modelApplyReportingPeriod: nextApplyPeriod,
                                            sections: current.sections.filter((section) => section.model !== modelKey),
                                        };
                                    })} />
                                    {appliesPeriod
                                        ? model.timestampColumns.length > 0 && renderPeriodBounds(report, modelKey, timestampColumns, basedOnColumn)
                                        : <p className="rc-hint rc-model-period-off">Reporting period is off for this data model: its metrics read every date, unless a metric sets its own date filter on the Metrics tab.</p>}
                                </div>
                            );
                        })}
                    </div>
                    <div className="rc-add-row rc-model-add">
                        <SelectPicker options={availableModels.map((option) => ({ id: option.dataModelId || option.key, value: String(option.dataModelId || ""), name: option.label }))} value={modelToAdd} placeholder={dataModelsLoading ? "Loading Data Models..." : availableModels.length ? "Select Data Model..." : "All Data Models Added"} onChange={setModelToAdd} searchPlaceholder="Search Data Models..." disabled={dataModelsLoading || !!dataModelsError || !availableModels.length} />
                        <AddActionButton disabled={!selectedModelToAdd} onClick={() => {
                            if (!selectedModelToAdd) return;
                            replaceSelectedReport((current) => { const next = clone(current); attachModel(next, selectedModelToAdd.key, selectedModelToAdd.dataModelId); return next; });
                            setModelToAdd("");
                        }}>Add Data Model</AddActionButton>
                    </div>
                </section>
                <ReportFiltersEditor
                    report={report}
                    invoiceNumber={reportInvoiceNumber}
                    setInvoiceNumber={setReportInvoiceNumber}
                    updateReport={replaceSelectedReport}
                    addChipValue={addChipValue}
                    removeChipValue={removeChipValue}
                />
            </>
        );
    }
    return renderSetupTab(report);
}
